import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from '../../icons';
import {
  listMyReservations, cancelReservation, type ReservationDto,
} from '../../api/reservation';
import { tRsv, tRsvStatus, tRsvDuration, getRsvLang, subscribeRsvLang } from '../../lib/reservation-i18n';
import { dayKey, fmtDate, fmtTime } from './reservation-calendar';
import { bucketOf, summarizeMine, waitingDays, type MineBucket } from './reservation-week';
import { ReservationAside } from './ReservationAside';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import './ReservationPage.css';

/**
 * 나의 예약 — 시안 `예약 화면 재설계` 반영 (2026-08-20).
 *
 * 바뀐 것:
 *   · 맨 위가 "다음 예약" 이다 — 이 화면에 들어오는 가장 큰 이유에 먼저 답한다.
 *   · 상태 필터를 ② 좌측 상세로 내리고, 본문은 **시간 축**(오늘 / 이번 주 / 이후 / 지난)으로 묶는다.
 *   · 지난 예약엔 "같은 조건으로 예약", 반려엔 "다시 신청" 처럼 다음 행동을 행 안에 둔다.
 *
 * 필터를 클라이언트에서 거는 이유: 좌측 칩이 건수를 함께 보여주는데, 서버 `status` 필터로 나눠
 * 받으면 화면에 있는 것과 세는 것이 어긋난다. 받아 온 범위 안에서 세고 거른다(더 보기로 확장).
 * 취소 가능 판정의 최종 권한은 서버(canTransition)에 있고, 화면은 pending/approved 만 버튼을 낸다.
 */

type PeriodFilter = 'upcoming' | 'past' | 'all';

/**
 * 상태 축 — 서버 상태 4종 + 파생 1종.
 * `done`(사용 완료) 은 서버 값이 아니라 `approved && endAt <= now` 다. 확정 안에 섞어 두면
 * "확정 7건" 을 눌렀을 때 이미 끝난 것까지 나와 지금 잡혀 있는 게 몇 건인지 알 수 없다.
 */
type StatusFilter = '' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'done';

const STATUS_KEYS: ReadonlyArray<Exclude<StatusFilter, ''>> = [
  'pending', 'approved', 'done', 'rejected', 'cancelled',
];

const GROUP_ORDER: MineBucket[] = ['today', 'week', 'later', 'past'];

/** 요일 짧은 이름 — 표시 언어를 따른다(월 뷰의 고정 한글 배열과 달리 여기는 행마다 다른 날짜다). */
function dowLabel(d: Date, lang: string): string {
  try {
    return new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d);
  } catch {
    return '';
  }
}

export function MyReservationsPage() {
  const lang = useSyncExternalStore(subscribeRsvLang, getRsvLang, getRsvLang);
  const [rows, setRows] = useState<ReservationDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('');
  // 상태를 누르는 순간 기간이 저절로 풀렸는지 — 사용자가 뭘 눌렀는지 잃지 않게 알린다.
  const [periodUnlocked, setPeriodUnlocked] = useState(false);
  const [period, setPeriod] = useState<PeriodFilter>('upcoming');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async (cursor?: string) => {
    setLoading(true);
    try {
      const res = await listMyReservations({ ...(cursor ? { cursor } : {}), limit: 50 });
      setRows((cur) => (cursor ? [...cur, ...res.reservations] : res.reservations));
      setNextCursor(res.nextCursor);
      setError(null);
    } catch {
      setError(tRsv('rsv.mine.err'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function cancel(r: ReservationDto) {
    if (!window.confirm(tRsv('rsv.mine.cancel_confirm'))) return;
    setBusyId(r.id);
    try {
      const updated = await cancelReservation(r.id);
      setRows((cur) => cur.map((x) => (x.id === updated.id ? updated : x)));
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tRsv('rsv.mine.cancel_err'));
    } finally {
      setBusyId(null);
    }
  }

  const summary = useMemo(() => summarizeMine(rows, now), [rows, now]);

  const matchStatus = useCallback((r: ReservationDto, f: StatusFilter): boolean => {
    if (f === '') return true;
    const ended = new Date(r.endAt).getTime() <= now.getTime();
    if (f === 'done') return r.status === 'approved' && ended;
    if (f === 'approved') return r.status === 'approved' && !ended;
    return r.status === f;
  }, [now]);

  const matchPeriod = useCallback((r: ReservationDto, p: PeriodFilter): boolean => {
    if (p === 'all') return true;
    const isPast = bucketOf(r, now) === 'past';
    return p === 'past' ? isPast : !isPast;
  }, [now]);

  /**
   * 칩에 적는 건수 = **지금 조건에서 실제로 보일 수**.
   * 전체 기준으로 세면 "반려 1" 을 눌렀는데 0건이 나오는 모순이 생긴다(옛 화면의 버그).
   */
  const countUnder = useCallback((p: PeriodFilter, f: StatusFilter): number => rows.filter((r) => {
    if (category && r.resource?.category !== category) return false;
    return matchStatus(r, f) && matchPeriod(r, p);
  }).length, [rows, category, matchStatus, matchPeriod]);

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const c = r.resource?.category;
      if (!c) continue;
      map.set(c, (map.get(c) ?? 0) + 1);
    }
    return [...map.entries()];
  }, [rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (category && r.resource?.category !== category) return false;
    return matchStatus(r, status) && matchPeriod(r, period);
  }), [rows, status, category, period, matchStatus, matchPeriod]);

  /**
   * 상태를 고를 때, 그 상태가 지금 기간에서 0건이면 **기간을 자동으로 풀어** 준다.
   * 반려·취소는 `bucketOf` 가 전부 '지난 것' 으로 보내므로 기본 기간('다가오는')에서는
   * 무조건 0건이었다 — 눌러도 아무 일이 없던 원인.
   */
  function pickStatus(next: StatusFilter) {
    const unlock = next !== '' && countUnder(period, next) === 0 && countUnder('all', next) > 0;
    if (unlock) setPeriod('all');
    setPeriodUnlocked(unlock);
    setStatus(next);
  }

  function resetFilters() {
    setStatus(''); setPeriod('upcoming'); setCategory(''); setPeriodUnlocked(false);
  }

  const groups = useMemo(() => {
    const map = new Map<MineBucket, ReservationDto[]>();
    for (const r of visible) {
      const b = bucketOf(r, now);
      const arr = map.get(b) ?? [];
      arr.push(r);
      map.set(b, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    }
    // 지난 것은 최근 것이 위로.
    map.get('past')?.reverse();
    return GROUP_ORDER
      .filter((b) => (map.get(b)?.length ?? 0) > 0)
      .map((b) => ({ bucket: b, rows: map.get(b) ?? [] }));
  }, [visible, now]);

  function groupLabel(b: MineBucket): string {
    if (b === 'today') return tRsv('rsv.mine.group_today', { date: fmtDate(now) });
    if (b === 'week') return tRsv('rsv.mine.group_week');
    if (b === 'later') return tRsv('rsv.mine.group_later');
    return tRsv('rsv.mine.group_past');
  }

  function stripeClass(r: ReservationDto): string {
    if (r.status === 'approved') return 'is-ok';
    if (r.status === 'pending') return 'is-wait';
    if (r.status === 'rejected') return 'is-no';
    return '';
  }

  function pillClass(r: ReservationDto): string {
    if (r.status === 'approved') return 'rsv-pill--ok';
    if (r.status === 'pending') return 'rsv-pill--wait';
    if (r.status === 'rejected') return 'rsv-pill--no';
    return 'rsv-pill--off';
  }

  /** 시각 칸 — 오늘이면 시각을 크게, 다른 날이면 날짜를 크게. */
  function whenParts(r: ReservationDto): { big: string; sub: string } {
    const start = new Date(r.startAt);
    const end = new Date(r.endAt);
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    if ((r.resource?.bookingUnit ?? 'time') === 'day') {
      return {
        big: `${start.getMonth() + 1}/${start.getDate()} (${dowLabel(start, lang)})`,
        sub: `${tRsv('rsv.mine.allday')} · ${tRsvDuration(minutes)}`,
      };
    }
    if (dayKey(start) === dayKey(now)) {
      return { big: fmtTime(start), sub: `–${fmtTime(end)} · ${tRsvDuration(minutes)}` };
    }
    return {
      big: `${start.getMonth() + 1}/${start.getDate()} (${dowLabel(start, lang)})`,
      sub: `${fmtTime(start)}–${fmtTime(end)}`,
    };
  }

  const nextPill = summary.next
    ? (summary.nextInMinutes > 0
      ? tRsv('rsv.mine.card_next_in', { dur: tRsvDuration(summary.nextInMinutes) })
      : tRsv('rsv.mine.card_next_now'))
    : tRsv('rsv.mine.card_next_none');

  return (
    <div className="rsv-screen">
      <ReservationAside>
        <div className="rsv-aside__body">
          <div className="rsv-grouphead">{tRsv('rsv.mine.section_status')}</div>
          <div className="rsv-fchips">
            <button
              type="button"
              className={`rsv-fchip${status === '' ? ' is-on' : ''}`}
              onClick={() => pickStatus('')}
            >
              {tRsv('rsv.mine.chip_count', { label: tRsv('rsv.mine.filter_all'), n: countUnder(period, '') })}
            </button>
            {STATUS_KEYS.map((s) => {
              const here = countUnder(period, s);
              const anywhere = countUnder('all', s);
              return (
                <button
                  key={s}
                  type="button"
                  className={[
                    'rsv-fchip',
                    status === s ? 'is-on' : '',
                    anywhere === 0 ? 'is-dim' : '',
                  ].filter(Boolean).join(' ')}
                  disabled={anywhere === 0}
                  onClick={() => pickStatus(s)}
                >
                  {tRsv('rsv.mine.chip_count', {
                    label: s === 'done' ? tRsv('rsv.mine.used') : tRsvStatus(s),
                    n: here,
                  })}
                </button>
              );
            })}
          </div>

          <div className="rsv-grouphead">{tRsv('rsv.mine.section_period')}</div>
          <div className="rsv-fchips">
            {(['upcoming', 'past', 'all'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={`rsv-fchip${period === p ? ' is-on' : ''}`}
                onClick={() => setPeriod(p)}
              >
                {p === 'upcoming' ? tRsv('rsv.mine.period_upcoming')
                  : p === 'past' ? tRsv('rsv.mine.period_past')
                    : tRsv('rsv.mine.period_all')}
              </button>
            ))}
          </div>

          {periodUnlocked && status !== '' && (
            <p className="rsv-aside__note" role="status">
              {tRsv('rsv.mine.period_unlocked', {
                label: status === 'done' ? tRsv('rsv.mine.used') : tRsvStatus(status),
              })}
            </p>
          )}

          {categories.length > 0 && (
            <>
              <div className="rsv-grouphead">{tRsv('rsv.mine.section_category')}</div>
              <div className="rsv-fchips">
                {categories.map(([c, n]) => (
                  <button
                    key={c}
                    type="button"
                    className={`rsv-fchip${category === c ? ' is-on' : ''}`}
                    onClick={() => setCategory((cur) => (cur === c ? '' : c))}
                  >
                    {tRsv('rsv.mine.chip_count', { label: c, n })}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </ReservationAside>

      <section className="rsv-content">
        {/*
          ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
             예약은 레일 제목(`SecondaryPane`)만 있고 **화면 머리가 통째로 없었다** —
             경로 · 제목 · 한 줄 설명 · 도움말이 다 빠져 있었다.
          ★ 아래 `.rsv-chead` 는 **고른 자원의 머리**라 다른 물건이다 — 그대로 둔다.
        */}
        <PageHeader
          className="rsv-head"
          breadcrumb={[tRsv('rsv.title'), tRsv('rsv.nav.mine')]}
          title={tRsv('rsv.nav.mine')}
          description={tRsv('rsv.head.desc_mine')}
          help={{
            title: tRsv('rsv.head.help_title'),
            body: (
              <>
              <p>내가 잡은 것과 신청한 것이 모두 보입니다 — 상태로 걸러 봅니다.</p>
              <p><b>취소</b>는 시작 전까지 됩니다. 지난 예약은 기록으로만 남습니다.</p>
              </>
            ),
          }}
        />
        <header className="rsv-chead">
          <div className="rsv-chead__t">
            <strong>{tRsv('rsv.mine.heading')}</strong>
            <span>
              {status === '' && period === 'upcoming' && category === ''
                ? tRsv('rsv.mine.count', { n: rows.length })
                : tRsv('rsv.mine.scope_label', {
                  status: status === '' ? tRsv('rsv.mine.filter_all')
                    : status === 'done' ? tRsv('rsv.mine.used') : tRsvStatus(status),
                  period: period === 'upcoming' ? tRsv('rsv.mine.period_upcoming')
                    : period === 'past' ? tRsv('rsv.mine.period_past') : tRsv('rsv.mine.period_all'),
                  n: visible.length,
                })}
            </span>
          </div>
          <div className="rsv-chead__sp" />
          {(status !== '' || period !== 'upcoming' || category !== '') && (
            <button type="button" className="rsv-btn rsv-btn--sm" onClick={resetFilters}>
              {tRsv('rsv.mine.reset')}
            </button>
          )}
          <Link className="rsv-btn rsv-btn--primary rsv-btn--sm" to="/reservation/book">
            <Plus size={12} aria-hidden /> {tRsv('rsv.mine.new')}
          </Link>
        </header>

        <div className="rsv-listview scrollbar-thin">
          {error && <p className="rsv-error" role="alert">{error}</p>}

          <div className="rsv-sum">
            <div className="rsv-sumcard rsv-sumcard--hero">
              <span className="rsv-sumcard__l">{tRsv('rsv.mine.card_next')}</span>
              <span className="rsv-sumcard__v">{nextPill}</span>
              <span className="rsv-sumcard__s">
                {summary.next
                  ? `${summary.next.resource?.name ?? '-'} · ${fmtTime(new Date(summary.next.startAt))}–${fmtTime(new Date(summary.next.endAt))}`
                  : tRsv('rsv.mine.empty')}
              </span>
            </div>
            <div className="rsv-sumcard">
              <span className="rsv-sumcard__l">{tRsv('rsv.mine.card_pending')}</span>
              <span className="rsv-sumcard__v">{tRsv('rsv.mine.count_n', { n: summary.pendingCount })}</span>
              <span className="rsv-sumcard__s">
                {summary.pendingCount === 0
                  ? '-'
                  : summary.pendingOldestDays > 0
                    ? tRsv('rsv.mine.card_pending_oldest', { n: summary.pendingOldestDays })
                    : tRsv('rsv.mine.card_pending_today')}
              </span>
            </div>
            <div className="rsv-sumcard">
              <span className="rsv-sumcard__l">{tRsv('rsv.mine.card_week')}</span>
              <span className="rsv-sumcard__v">
                {tRsv('rsv.mine.card_week_value', {
                  n: summary.weekCount,
                  dur: tRsvDuration(summary.weekMinutes) || '-',
                })}
              </span>
              <span className="rsv-sumcard__s">
                {categories.map(([c, n]) => `${c} ${n}`).join(' · ') || '-'}
              </span>
            </div>
          </div>

          {loading && rows.length === 0 ? (
            <p className="rsv-empty">{tRsv('rsv.cal.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="rsv-empty">{tRsv('rsv.mine.empty')}</p>
          ) : groups.length === 0 ? (
            <p className="rsv-empty">{tRsv('rsv.mine.none_in_view')}</p>
          ) : groups.map(({ bucket, rows: groupRows }) => (
            <div key={bucket} className="rsv-tl">
              <div className="rsv-tl__h">{groupLabel(bucket)}</div>
              {groupRows.map((r) => {
                const when = whenParts(r);
                const done = r.status === 'approved' && new Date(r.endAt).getTime() <= now.getTime();
                return (
                  <div key={r.id} className="rsv-item">
                    <div className={`rsv-item__stripe ${done ? '' : stripeClass(r)}`} />
                    <div className="rsv-item__b">
                      <div className="rsv-item__when">
                        <b>{when.big}</b>
                        <span>{when.sub}</span>
                      </div>
                      <div className="rsv-item__m">
                        <b>
                          {r.resource?.name ?? '-'}
                          {r.resource?.location && <em> · {r.resource.location}</em>}
                        </b>
                        {r.purpose && <span>{r.purpose}</span>}
                        {r.quantity > 1 && (
                          <span>{tRsv('rsv.book.quantity')} {r.quantity}</span>
                        )}
                        {r.status === 'rejected' && r.rejectReason && (
                          <span className="rsv-item__reason">
                            {tRsvStatus('rejected')} · {r.rejectReason}
                          </span>
                        )}
                        {r.status === 'pending' && waitingDays(r.createdAt, now) > 0 && (
                          <span>{tRsv('rsv.approve.days', { n: waitingDays(r.createdAt, now) })}</span>
                        )}
                      </div>
                      <div className="rsv-item__a">
                        <span className={`rsv-pill ${done ? 'rsv-pill--off' : pillClass(r)}`}>
                          {done ? tRsv('rsv.mine.used') : tRsvStatus(r.status)}
                        </span>
                        {(r.status === 'pending' || (r.status === 'approved' && !done)) && (
                          <button
                            type="button"
                            className="rsv-btn rsv-btn--sm"
                            disabled={busyId === r.id}
                            onClick={() => void cancel(r)}
                          >
                            {tRsv('rsv.mine.cancel')}
                          </button>
                        )}
                        {(r.status === 'rejected' || r.status === 'cancelled' || done) && r.resource && (
                          <Link
                            className="rsv-btn rsv-btn--sm"
                            to={`/reservation/book?resource=${encodeURIComponent(r.resource.id)}`}
                          >
                            {r.status === 'rejected' ? tRsv('rsv.mine.reapply') : tRsv('rsv.mine.rebook')}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {nextCursor && (
            <button
              type="button"
              className="rsv-btn rsv-more"
              disabled={loading}
              onClick={() => void load(nextCursor)}
            >
              {tRsv('rsv.mine.more')}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
